import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  Typography,
  IconButton,
  Pagination,
  Box,
  Grid,
  Button,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  styled,
} from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import NavigationTab from "../navigation";
import { Model } from "./modelinterface";
import AddModelDialog from "./addmodel";
import EditModelDialog from "./updatemodel";

const StyledTableCell = styled(TableCell)(() => ({
  fontWeight: "bold",
  color: "white",
}));

const ModelListTable: React.FC = () => {
  const [allmodels, setAllModels] = useState<Model[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [modelToToggle, setModelToToggle] = useState<{ id: number; status: boolean } | null>(null);

  const fetchModels = (currentPage: number) => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/model?page=${currentPage}&pageSize=${pageSize}`)
      .then((response) => response.json())
      .then((data) => {
        // Sort users by ID in ascending order
        const sortedModels = (data.items || []).sort((a: Model, b: Model) => a.id - b.id);
        setModels(sortedModels);
        setTotalCount(data.totalCount || 0);
      })
      .catch((error) => console.error("Error fetching users:", error));
  };

  useEffect(() => {
    fetchModels(page);
  }, [page]);

  const handleModelAdded = () => {
    fetchModels(page);
  };

  const handleModelUpdated = () => {
    fetchModels(page);
  };

  const handleEditModel = (model: Model) => {
    setSelectedModel(model);
    setEditDialogOpen(true);
  };

  const handleStatusChangeConfirm = () => {
    if (!modelToToggle) return;
    
    fetch(`https://kineticgreen-backend.navigolabs.com/model/userStatusChangeWTHT/${modelToToggle.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ status: !modelToToggle.status }),
    })
      .then((response) => response.json())
      .then(() => {
        fetchModels(page);
        setStatusDialogOpen(false);
      })
      .catch((error) => console.error("Error updating status:", error));
  };

  const handleStatusChangeRequest = (modelId: number, currentStatus: boolean) => {
    setModelToToggle({ id: modelId, status: currentStatus });
    setStatusDialogOpen(true);
  };

  return (
    <Box sx={{ background: "#1a1a1a", paddingBottom: 8 }}>
      <NavigationTab />
      <Grid container>
        <Grid item xs={12} sx={{ display: "flex", justifyContent: "center", alignItems: "center", background: "transparent" }}>
          <Box sx={{ width: "1200px", background: "black", border: "1px solid white", borderRadius: "18px" }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px" }}>
              <Typography variant="h6" sx={{ color: "white" }}>User List</Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                sx={{ borderRadius: "20px", background: "black", color: "white", border: "2px green solid" }}
                onClick={() => setDialogOpen(true)}
              >
                Add User
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ background: "green" }}>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>Active/Inactive</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>User Name</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>ROLE</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>STATUS</TableCell>
                    <TableCell sx={{ color: "white", fontWeight: "bold" }}>ACTION</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {models.map((model, index) => (
                    <TableRow key={index}>
                      <StyledTableCell>
                        <Switch
                          checked={model.status}
                          onChange={() => handleStatusChangeRequest(model.id, model.status)}
                          color="primary"
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Box sx={{ paddingRight: 3 }}>
                            <Avatar sx={{ bgcolor: "white", color: "black" }} />
                          </Box>
                          <Box sx={{ textAlign: "left" }}>
                            <Typography variant="body1" sx={{ color: "white" }}>{model.name}</Typography>
                            <Typography variant="body2" sx={{ color: "lightgray" }}>ID {model.id}</Typography>
                          </Box>
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell sx={{ color: "white" }}>{model.role.name}</StyledTableCell>
                      <StyledTableCell>
                        <Typography sx={{ color: "white" }}>
                          {model.status ? "Active" : "Inactive"}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <IconButton onClick={() => handleEditModel(model)}>
                          <Edit sx={{ color: "white" }} />
                        </IconButton>
                        <IconButton>
                          <Delete sx={{ color: "white" }} />
                        </IconButton>
                      </StyledTableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2, mb: 2 }}>
              <Pagination
                count={Math.ceil(totalCount / pageSize)}
                page={page}
                onChange={(event, value) => setPage(value)}
                color="primary"
                sx={{
                  "& .MuiPaginationItem-root": {
                    color: "white",
                    backgroundColor: "green",
                    borderRadius: "8px",
                    margin: "0 4px",
                  },
                  "& .MuiPaginationItem-root.Mui-selected": {
                    backgroundColor: "black",
                    color: "white",
                  },
                  "& .MuiPaginationItem-root:hover": {
                    backgroundColor: "darkgreen",
                  },
                }}
              />
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Confirm Status Change</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to {modelToToggle?.status ? "deactivate" : "activate"} this user?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)} color="secondary">Cancel</Button>
          <Button onClick={handleStatusChangeConfirm} color="primary">Confirm</Button>
        </DialogActions>
      </Dialog>

      <AddModelDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onModelAdded={handleModelAdded} />
      <EditModelDialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} model={selectedModel} onModelUpdated={handleModelUpdated} />
    </Box>
  );
};

export default ModelListTable;
    